import {
  getActiveSessionMetadata,
  withSessionMetadata,
} from "./session-metadata";
import {
  getAdditionalDataRequiredFields,
  getPrismicCollectionDocument,
  getPrismicTaskInstructions,
} from "./prismic";
import { apiGetUserProfile } from "./api";
import { captureDB } from "./indexed-db";
import { parseKeyValueLines } from "./key-value-lines";
import { PrismicCollectionDetailField, PrismicCollectionDetails } from "types";

export interface StoredCollectionDetails {
  device?: string;
  camera_not_flipped?: boolean;
  labels?: Record<string, string | string[]>;
  data?: string[];
}

export function buildCaptureCollectionDetails({
  fields,
  sharedDetails,
  captureDetails,
  requiredFields,
  allRequiredFields,
}: {
  fields: PrismicCollectionDetailField[];
  sharedDetails: StoredCollectionDetails | null;
  captureDetails: Array<{ label_id: string; value: string }>;
  requiredFields: string[];
  allRequiredFields: string[];
}): StoredCollectionDetails | null {
  if (!sharedDetails && fields.length === 0) return null;

  const values = {
    ...Object.fromEntries(
      captureDetails.map(({ label_id, value }) => [label_id, value])
    ),
    ...sharedDetails?.labels,
  };
  // Rebuild the child form's labels in its configured order. Shared inputs
  // supply values, but must not introduce fields belonging to other children.
  const labels = Object.fromEntries(
    fields.flatMap((field) => {
      const id = field.label_id?.toString().trim();
      if (!id) return [];
      const value = values[id];
      if (value === undefined) return [];
      const storedValue =
        field.label_type === "dropdown"
          ? parseKeyValueLines(field.dropdown_options).find(
              (option) => option.key === value || option.value === value
            )?.value ?? value
          : value;
      return [[id, storedValue]];
    })
  );
  const profileFields = new Set(
    allRequiredFields.map((field) => field.toLowerCase())
  );
  const data = sharedDetails?.data;

  return {
    device: sharedDetails?.device,
    ...(sharedDetails && "camera_not_flipped" in sharedDetails
      ? { camera_not_flipped: sharedDetails.camera_not_flipped }
      : {}),
    labels,
    data: data && [
      ...data.filter(
        (item) => !profileFields.has(item.split(":", 1)[0].trim().toLowerCase())
      ),
      ...requiredFields.flatMap((field) => {
        const entry = data.find((item) =>
          item.toLowerCase().startsWith(`${field.toLowerCase()}:`)
        );
        return entry === undefined ? [] : [entry];
      }),
    ],
  };
}

export async function getCollectionDetails(
  taskId?: string,
  storedDetailsOverride?: StoredCollectionDetails | null,
  selectedCollection?: PrismicCollectionDetails,
  sessionMetadata = getActiveSessionMetadata()
): Promise<{
  collection_details?: Array<string>;
  exif?: object;
} | void> {
  const result: {
    collection_details?: Array<string>;
    exif?: object;
  } = {};

  try {
    // Read collection details from IndexedDB step 0 for task isolation
    let stored: StoredCollectionDetails | null = storedDetailsOverride ?? null;

    if (taskId && storedDetailsOverride === undefined) {
      const storedCaptures = await captureDB.getStepCaptures(taskId);
      const step0 = storedCaptures.find((s) => s.stepNumber === 0);
      if (step0?.collectionDetails) {
        stored = step0.collectionDetails;
      }
    }

    const hadStoredDetails = Boolean(stored);
    stored = withSessionMetadata(stored, sessionMetadata) || null;

    if (stored) {
      const collection =
        selectedCollection ?? (await getPrismicCollectionDocument());

      if (!collection) {
        return;
      }
      const prismic = collection.data;

      result.collection_details = [];

      if (prismic.devices.length > 0 && stored.device) {
        result.collection_details.push(stored.device);

        if ("camera_not_flipped" in stored) {
          result.exif = {
            camera_not_flipped: stored.camera_not_flipped,
          };
        }
      }

      // Get required fields for this task
      let requiredFields: string[] = [];
      if (taskId) {
        try {
          const taskInstructions = await getPrismicTaskInstructions(
            taskId,
            collection
          );
          requiredFields = getAdditionalDataRequiredFields(taskInstructions);
        } catch (err) {
          console.error(
            "[getCollectionDetails] Error fetching task requirements:",
            err
          );
        }
      }

      // Without a shared setup form, profile fields have not been saved yet.
      if (
        (storedDetailsOverride || !hadStoredDetails) &&
        stored.data === undefined &&
        requiredFields.length > 0
      ) {
        const profile = await apiGetUserProfile();
        stored = {
          ...stored,
          data: requiredFields
            .filter(
              (field) =>
                profile[field] !== undefined &&
                profile[field] !== null &&
                profile[field] !== ""
            )
            .map((field) => `${field}: ${profile[field]}`),
        };
      }

      // Include all form labels (date, dropdown, etc.)
      // But exclude additional data fields (height, weight, eye color) unless they're required
      const additionalDataFields = ["height", "weight", "eye color"];

      if (stored.labels) {
        Object.entries(stored.labels).forEach(([key, value]) => {
          // Skip additional data fields unless they're in the required fields list
          if (
            additionalDataFields.includes(key) &&
            !requiredFields.includes(key)
          ) {
            return;
          }

          if (value && value !== "") {
            const formattedValue = `${key}: ${value}`;
            result.collection_details?.push(formattedValue);
          }
        });
      }

      // Filter the data array to only include required additional data
      if (stored.data && Array.isArray(stored.data)) {
        const filteredData =
          requiredFields.length > 0
            ? stored.data.filter((item: string) =>
                requiredFields.some((field) =>
                  item.toLowerCase().startsWith(`${field.toLowerCase()}:`)
                )
              )
            : stored.data;

        result.collection_details = [
          ...result.collection_details,
          ...filteredData,
        ];
      } else {
        result.collection_details = [...result.collection_details];
      }

      // Flatten arrays and filter out empty values
      result.collection_details = result.collection_details.flat();
      result.collection_details = result.collection_details.filter(
        (i) => typeof i === "string"
      );

      return result;
    } else {
      // No collection details in localStorage, but we might still need additional data
      // If we have a taskId, check if this task requires additional data
      if (taskId) {
        try {
          // Fetch task instructions to get required additional data fields
          const requiredFields = getAdditionalDataRequiredFields(
            await getPrismicTaskInstructions(taskId, selectedCollection)
          );

          if (requiredFields.length > 0) {
            // Fetch user profile data
            const userProfile = await apiGetUserProfile();

            // Include only the required fields from additional data
            const additionalDataEntries = requiredFields
              .filter(
                (field) =>
                  userProfile[field] !== undefined &&
                  userProfile[field] !== null &&
                  userProfile[field] !== ""
              )
              .map((field) => `${field}: ${userProfile[field]}`);

            if (additionalDataEntries.length > 0) {
              result.collection_details = additionalDataEntries;
              return result;
            }
          }
        } catch (err) {
          console.error(
            "[getCollectionDetails] Error fetching task instructions or user profile:",
            err
          );
        }
      }

      // No data to return
      return;
    }
  } catch (err) {
    console.error("[getCollectionDetails] Error:", err);
  }
}
